"use client";

import React, { useEffect, useMemo } from "react";
import { dateListOffer } from "@/lib/Types/DateListResponse/DateListResponse";
import styled from "styled-components";
import OfferListItem from "./OfferListItem/OfferListItem";
import OfferListItemErrorBoundary from "./OfferListItem/OfferListItemErrorBoundary";
import { usePathname } from "next/navigation";
import { Button } from "@lmn/design-system";
import OfferListSkeleton, { OfferListItemSkeleton } from "./OfferListSkeleton";
import { useDateListTracking } from "@/lib/PlatformData/hooks/useDateListTracking";
import useTracking from "@/lib/startrack/hooks/useTracking";
import { StartrackingEventsNames } from "@/lib/startrack/trackingEvents";
import { useUpdateParam } from "@/lib/Hooks/useUpdateParam";
import AdSlot from "@/lib/AdManagment/AdSlot";
import { getIsLoggedIn } from "@/lib/Helpers/userHelpers";
import { useDateListStore } from "@/lib/stores/dateListStore";
import NoResultsFound from "@/components/Stateful/NoResultsFound/NoResultsFound";
import { useDateListSearch, PAGE_SIZE } from "@/app/api/useDateListSearch";
import { channel } from "@/lib/Types/Channel";
import { createAndAppendClickId } from "../Helpers/createAndAppendClickId";
import { storeOseTrackingId } from "@/lib/Helpers/manageOseTrackingId";
import { AppConfig } from "@/lib/Types/AppConfig";
import { sendKibanaLog } from "@/app/api/sendKibanaLog";

// Excluded tour operators for QuickVacancy prefetch (from old app)
const EXCLUDED_TOUR_OPERATORS = ["LMX", "XLMX", "LMXF", "LMXI", "LMIF"];

const AD_POSITIONS = {
  FIRST: 1,
  SECOND: 4,
};

function getItemOrder(index: number): number {
  if (index < AD_POSITIONS.FIRST) return index;
  if (index < AD_POSITIONS.SECOND) return index + 1;
  return index + 3;
}

const OfferContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
`;

const OfferItem = styled.div<{ $order: number }>`
  order: ${({ $order }) => $order};
`;

const SkeletonItem = styled.div<{ $order: number }>`
  order: ${({ $order }) => $order};
`;

const LoadMoreButton = styled(Button)`
  margin-top: 3rem;
  display: block;
  margin-inline: auto;
`;

interface OfferListProps {
  appConfig: AppConfig;
}

export default function OfferListClientQuery({ appConfig }: OfferListProps) {
  const {
    data: dateListData,
    isFetching,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    searchParams,
  } = useDateListSearch(appConfig);

  // Only update URL searchId when we have data from the backend
  const searchIdToUpdate =
    dateListData?.searchId || searchParams?.get("searchId") || "";
  useUpdateParam("searchId", searchIdToUpdate);

  const { track } = useTracking(appConfig);
  const { WlConfig, userData, viewMode } = appConfig;
  const isLoggedIn = getIsLoggedIn();
  const pathname = usePathname();
  const isHotelOnly = viewMode.channel === channel.HOTEL;

  // QuickVacancy prefetch configuration
  const prefetchCount = WlConfig?.quickVacancyPreFetchOffersCount ?? 0;
  const duration = searchParams.get("duration");
  const departureAirport = searchParams.get("departureAirport");

  // Determine if prefetch conditions are met
  const canPrefetch = useMemo(() => {
    if (prefetchCount === 0) return false;

    // Duration must be valid
    const durationValid = duration && duration !== "false";
    if (!durationValid) return false;

    // Airport must be specific (not default DE/AT)
    const airportValid =
      departureAirport &&
      departureAirport !== "DE" &&
      departureAirport !== "AT";
    if (!airportValid) return false;

    return true;
  }, [prefetchCount, duration, departureAirport]);

  // Helper to check if a specific offer should prefetch
  const shouldOfferPrefetch = (offer: dateListOffer, idx: number): boolean => {
    if (!canPrefetch) return false;
    if (idx >= prefetchCount) return false;

    // Skip excluded tour operators
    if (EXCLUDED_TOUR_OPERATORS.includes(offer?.tourOperator?.code))
      return false;

    // Skip if already has quickVacancy from backend cache
    if (offer.quickVacancy) return false;

    return true;
  };

  const firstOffer = dateListData?.dateList[0];
  const searchId = dateListData?.searchId ?? null;
  const cid = pathname.match(/cid_(\d+)/);
  const provider = firstOffer?.bookingInfo?.offerID?.includes("PEAKWORK")
    ? "PEAKWORK"
    : "AMADEUS";

  // Track with platform data
  useDateListTracking({
    appConfig,
    isLoading: isLoading || isFetching,
    dateListData: dateListData,
    cid: cid ? cid[1] : "",
    searchId,
    searchParams,
  });

  // Track with Startrack
  useEffect(() => {
    if (!dateListData || isLoading || isFetching) return;

    // Generate here click Id - it changes on every new data fetched
    // Used in ProductDetailClicked and useStartrackFilterTracking
    const clickId = createAndAppendClickId(dateListData?.searchId);

    storeOseTrackingId(`${WlConfig.oseTrackingIdBrand}_${clickId}`);

    track(StartrackingEventsNames.ProductDetailShown, {
      clickoutid: searchParams.get("trackingId") || null,
      searchId: dateListData?.searchId || "",
      productId: cid ? cid[1] : "",
      clickId: clickId,
      totalPrice: dateListData?.cheapestOffer?.estimatedTotalPrice || 0,
      provider,
      userUid: userData?.userUid ?? "",
      isLoggedIn,
      to: dateListData?.cheapestOffer?.tourOperator?.code || "",
      resulPageClickid: searchParams.get("resulPageClickid") || null,
      responseCode: dateListData?.trackingData?.responseCode || "",
      responseDetail: dateListData?.trackingData?.responseDetail || "",
      numberOfResults: dateListData?.trackingData?.numberOfResults ?? 0,
      numberOfResultsPeakwork:
        dateListData?.trackingData?.numberOfResultsPeakwork || 0,
      numberOfResultsAmadeus:
        dateListData?.trackingData?.numberOfResultsAmadeus || 0,
      priceCalendarShown:
        !searchParams.get("duration") ||
        searchParams.get("duration") === "0" ||
        viewMode.channel === channel.HOTEL,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateListData, isFetching, isLoading]);

  // Set priceList and hotelAlreadyBooked for shared state
  useEffect(() => {
    useDateListStore.setState({
      priceList: dateListData?.priceList ?? null,
      hotelAlreadyBooked: dateListData?.hotelAlreadyBooked ?? null,
    });
  }, [dateListData]);

  const handleLoadMore = () => {
    fetchNextPage();
    sendKibanaLog({ envConfig: appConfig.envConfig, message: "Load More Offers Button Clicked", category: "interaction", searchId: searchId ?? "" });
  };

  const showSkeleton = isLoading;
  const dateList = dateListData?.dateList ?? [];
  const itemCount = showSkeleton ? PAGE_SIZE : dateList.length;

  if (!isLoading && (dateList?.length === 0 || error)) {
    return <NoResultsFound alternativeOffersPresent={false} showFilterChips WlConfig={WlConfig} />;
  }

  return (
    <>
      <OfferContainer data-testid="hotel-offer-list">
        {/* Actual content */}
        {!showSkeleton &&
          dateList.map((offer: dateListOffer, idx: number) => (
            <OfferItem key={offer.offerId} $order={getItemOrder(idx)}>
              <OfferListItemErrorBoundary
                offer={offer}
                envConfig={appConfig.envConfig}
                debugMode={searchParams.get("debugMode") === "true"}
              >
                <OfferListItem
                  offer={offer}
                  searchId={searchId ?? ""}
                  idx={idx + 1}
                  shouldPrefetch={shouldOfferPrefetch(offer, idx)}
                  appConfig={appConfig}
                />
              </OfferListItemErrorBoundary>
            </OfferItem>
          ))}

        {/* Skeleton items when loading */}
        {showSkeleton &&
          [...Array(PAGE_SIZE)].map((_, index) => (
            <SkeletonItem
              key={`skeleton-${index}`}
              $order={getItemOrder(index)}
            >
              <OfferListItemSkeleton isHotelOnly={isHotelOnly} />
            </SkeletonItem>
          ))}

        {/* Always-mounted ad slots - positioned via CSS order */}
        <AdSlot
          slotId="div-gpt-ad-intrip"
          order={AD_POSITIONS.FIRST}
          hidden={itemCount <= AD_POSITIONS.FIRST}
        />
        <AdSlot
          slotId="div-gpt-ad-intrip2"
          order={AD_POSITIONS.SECOND}
          hidden={itemCount <= AD_POSITIONS.SECOND}
        />
      </OfferContainer>

      {isFetchingNextPage && (
        <OfferListSkeleton count={PAGE_SIZE} isHotelOnly={isHotelOnly} />
      )}

      {hasNextPage && (
        <LoadMoreButton
          variant="secondary"
          onClick={handleLoadMore}
          loading={isFetchingNextPage}
        >
          Weitere Ergebnisse anzeigen
        </LoadMoreButton>
      )}
    </>
  );
}
